import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
  useFetchOnlineInventory,
  useFetchOnlineInventoryArchived,
  useFetchOnlineInventoryProgrammed,
} from '../../../../hooks/onlineInventory/useOnlineInventory';
import { useLocation, useNavigate } from 'react-router-dom';
import OnlineInventoryView from '../View/OnlineInventoryView';
import OnlineInventoryArchivedView from '../View/OnlineInventoryArchivedView';
import AdvancedFiltersView from '../View/AdvancedFiltersView';
import { OnlineInventoryAPI } from '../../api/onlineInventoryAPI';
import NewsReportView from '../View/NewsReportView';
import OnlineInventoryVerify from '../View/OnlineInventoryVerify';
import OnlineInventoryDetailView from '../View/OnlineInventoryDetailView';
import FisicalLocationView from '../View/FisicalLocation';
import PhysicalVerification from '../View/PhysicalVerification';
import ManageState from '../View/ManageState';
import OnlineInventoryProgrammedView from '../View/OnlineInventoryProgrammedView';

const OnlineInventoryContainer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isArchivedView = location.pathname.includes('onlineinventoryarchived');
  const isOnlineinventoryProgrammed = location.pathname.includes('onlineinventory-programmed');

  const formikProps = useFormik({
    initialValues: {
      newResponsible: '',
      status_id: '',
    },
    onSubmit: (values) => {
    },
  });

  const {
    data: movements,
    isPending,
    refetch,
  } = isArchivedView
    ? useFetchOnlineInventoryArchived()
      : isOnlineinventoryProgrammed 
        ? useFetchOnlineInventory()
          : useFetchOnlineInventory(formikProps.values);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [selectedRecordFiscal, setSelectedRecordFiscal] = useState<any>(null);
  const [selectedNewsReport, setSelectedNewsReport] = useState<any>(null);
  const [selectedVerification, setSelectedVerification] = useState<any>(null);
  const [selectedManageState, setSelectedManageState] = useState<any>(null);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [isModalVisibleFiscal, setIsModalVisibleFiscal] = useState(false);
  const [isModalNewsReport, setIsModalNewsReport] = useState(false);
  const [isModalVerification, setIsModalVerification] = useState(false);
  const [isModalManageState, setIsModalManageState] = useState(false);
  const [isModalVerify, setIsModalVerify] = useState<any>(null);
  const [filters, setFilters] = useState<any>([]);

  const handleApplyFilters = (appliedFilters: any) => {
    setFilters(appliedFilters);
    setIsFilterModalVisible(false);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const handleFiscalLocation = (record: any) => {
    setSelectedRecordFiscal(record);
    setIsModalVisibleFiscal(true);
  };

  const handleCloseModalFiscal = () => {
    setIsModalVisibleFiscal(false);
    setSelectedRecordFiscal(null);
  };

  const handleNewsReport = (record: any) => {
    setSelectedNewsReport(record);
    setIsModalNewsReport(true);
  };

  const handleVerifyModal = () => {
    setIsModalVerify(true);
  };

  const handleCloseVerifyModal = () => {
    setIsModalVerify(false);
  };

  const handleCloseNewsReport = () => {
    setIsModalNewsReport(false);
    setSelectedNewsReport(null);
  };

  const handleVerification = (record: any) => {
    setSelectedVerification(record);
    setIsModalVerification(true);
  };

  const handleCloseVerification = () => {
    setIsModalVerification(false);
    setSelectedVerification(null);
  };

  const handleManageState = (record: any) => {
    setSelectedManageState(record);
    setIsModalManageState(true);
  };

  const handleCloseManageState = () => {
    setIsModalManageState(false);
    setSelectedManageState(null);
  };

  const handleOpenFilters = async () => {
    try {
      const filtersData = await OnlineInventoryAPI.fetchAdvancedFilters();
      setFilters(Array.isArray(filtersData) ? filtersData : []);
      setIsFilterModalVisible(true);
    } catch (error) {
      console.error('Error al obtener filtros avanzados:', error);
    }
  };

  useEffect(() => {
    refetch();
  }, [location.pathname]);

  return (
    <>
      {isArchivedView ? (
        <OnlineInventoryArchivedView
          movements={movements || []}
          isLoading={isPending}
          formikProps={formikProps}
          onViewCreate={() => navigate('/onlineinventoryarchived/create')}
          onOpenFilters={handleOpenFilters}
          onApplyFilters={handleApplyFilters}
          filters={filters}
          isFilterModalVisible={isFilterModalVisible}
          setIsFilterModalVisible={setIsFilterModalVisible}
        />
      ) : (
        isOnlineinventoryProgrammed ? (
          <OnlineInventoryProgrammedView
            movements={movements || []}
            isLoading={isPending}
            formikProps={formikProps}
            refetch={refetch}
            onViewReport={handleNewsReport}
            onViewVerification={handleVerification}
            onViewManageState={handleManageState}
            onViewFiscal={handleFiscalLocation}
          />
        ) : (
          <OnlineInventoryView
            movements={movements || []}
            isLoading={isPending}
            formikProps={formikProps}
            refetch={refetch}
            onViewReport={handleNewsReport}
            onViewVerify={handleVerifyModal}
          />
        )
      )}

      {selectedRecord && (
        <OnlineInventoryDetailView
          visible={isModalVisible}
          onClose={handleCloseModal}
          record={selectedRecord}
          inventoryCode={selectedRecord.inventory_code}
        />
      )}

      {selectedRecordFiscal && (
        <FisicalLocationView
          visible={isModalVisibleFiscal}
          onClose={handleCloseModalFiscal}
          record={selectedRecordFiscal}
          refetch={refetch}
        />
      )}

      {selectedNewsReport && (
        <NewsReportView
          visible={isModalNewsReport}
          onClose={handleCloseNewsReport}
          record={selectedNewsReport}
        />
      )}

      {selectedVerification && (
        <PhysicalVerification
          visible={isModalVerification}
          onClose={handleCloseVerification}
          record={selectedVerification}
          refetch={refetch}
        />
      )}

      {selectedManageState && (
        <ManageState
          visible={isModalManageState}
          onClose={handleCloseManageState}
          record={selectedManageState}
          refetch={refetch}
        />
      )}

      {isModalVerify && (
        <OnlineInventoryVerify
          visible={isModalVerify}
          onClose={handleCloseVerifyModal}
        />
      )}

      {isFilterModalVisible && (
        <AdvancedFiltersView
          onClose={() => setIsFilterModalVisible(false)}
          onApplyFilters={handleApplyFilters}
        />
      )}
    </>
  );
};

export default OnlineInventoryContainer;
